import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Plus, Sparkles, X } from "lucide-react";
import PageIntro from "../../components/PageIntro/PageIntro";
import { Button } from "../../components/ui/button";
import ItemPicker from "../../components/CbtQuestionRuns/ItemPicker";
import { RunStatusPill } from "../../components/CbtQuestionRuns/Pills";
import {
  COUNT_CHOICES,
  ITEM_TYPES,
  LEVELS,
  RUN_STATUS,
  describeError,
  inputClass,
  itemTypeLabel,
  levelLabel,
  runItemName,
  selectClass,
} from "../../components/CbtQuestionRuns/cbtQuestionRuns.config";
import {
  useCreateCbtRunMutation,
  useGetCbtRunsQuery,
  useSuggestCbtTopicsMutation,
} from "../../reactWrapper/Redux/apiSlice";
import UsageBar from "../../components/CbtQuestionRuns/UsageBar";
import StartHere from "../../components/CbtQuestionRuns/StartHere";

/**
 * CBT › Generate questions.
 *
 *   1. Pick a specialty, tool or skill, a level, and 15 or 30 questions at most.
 *   2. Agree the topics: each topic gets a number of questions (up to that maximum in total).
 *   3. Create the run. The cron writes it with Claude, Gemini checks it,
 *      and it appears under "Runs" ready for review.
 */
const cardClass = "rounded-xl border bg-card p-5";
const labelClass =
  "text-xs font-semibold uppercase tracking-wide text-brand-navy";

// A starting plan the admin edits
const DEFAULT_TOPIC_NAMES = [
  "Core knowledge and concepts",
  "Common tasks and workflows",
  "Complex scenarios and judgement",
  "Mistakes, risks and controls",
];

// Share `total` questions as evenly as possible across the topics.
// e.g. 30 over 4 topics -> 8, 8, 7, 7
function spreadCounts(topics, total) {
  const base = Math.floor(total / topics.length);
  const extra = total % topics.length; // the first `extra` topics get one more
  return topics.map((topic, index) => ({
    ...topic,
    count: index < extra ? base + 1 : base,
  }));
}

const DEFAULT_TOPICS = spreadCounts(
  DEFAULT_TOPIC_NAMES.map((name) => ({ name: name, count: 0 })),
  30,
);

export default function CbtQuestionGenerator() {
  const history = useHistory();
  const [itemType, setItemType] = useState("specialty");
  const [item, setItem] = useState(null); // { id, name } or null
  const [levelCode, setLevelCode] = useState(3);
  const [maxQuestions, setMaxQuestions] = useState(30); // 15 or 30
  const [topics, setTopics] = useState(DEFAULT_TOPICS);
  const [newTopic, setNewTopic] = useState("");

  const [createRun, createState] = useCreateCbtRunMutation();
  const [suggest, suggestState] = useSuggestCbtTopicsMutation();

  // The number of questions is the sum of the topic counts
  let total = 0;
  for (const topic of topics) {
    total = total + (Number(topic.count) || 0);
  }

  const hasEmptyName = topics.some((topic) => topic.name.trim() === "");
  const canCreate =
    item &&
    total >= 1 &&
    total <= maxQuestions &&
    !hasEmptyName &&
    !createState.isLoading;

  function changeType(key) {
    setItemType(key);
    setItem(null); // an item belongs to one type
  }

  // "Use this" in Start here: fill the form with that item and level
  function pickStartHere(entry) {
    setItemType(entry.itemType);
    setItem({ id: entry.itemId, name: entry.itemName });
    setLevelCode(entry.levelCode);
  }

  // Switching between 15 and 30 keeps the topic names and re-shares the counts
  function changeMaxQuestions(count) {
    setMaxQuestions(count);
    if (topics.length > 0) {
      setTopics(spreadCounts(topics, count));
    }
  }

  function changeTopic(index, field, value) {
    const updated = topics.map((topic, i) =>
      i === index ? { ...topic, [field]: value } : topic,
    );
    setTopics(updated);
  }

  function removeTopic(index) {
    setTopics(topics.filter((topic, i) => i !== index));
  }

  function addTopic(event) {
    event.preventDefault();
    const name = newTopic.trim();
    if (!name) {
      return;
    }
    setTopics([...topics, { name: name, count: 1 }]);
    setNewTopic("");
  }

  // Claude suggests topics for this item, level and number of questions.
  // They replace the current topics; the admin can still edit them.
  async function suggestTopics() {
    try {
      const response = await suggest({
        item_type: itemType,
        item_id: item.id,
        level_code: levelCode,
        question_count: maxQuestions,
      }).unwrap();

      setTopics(response.data.topics);
      toast.success(response.message);
    } catch (error) {
      toast.error(describeError(error));
    }
  }

  async function create() {
    // Topics with 0 questions are left out; the API needs at least 1 each
    const cleanTopics = topics
      .filter((topic) => Number(topic.count) > 0)
      .map((topic) => ({
        name: topic.name.trim(),
        count: Number(topic.count),
      }));

    try {
      const response = await createRun({
        item_type: itemType,
        item_id: item.id,
        level_code: levelCode,
        question_count: total,
        topics: cleanTopics,
      }).unwrap();

      toast.success(response.message);
      history.push(`/cbt/questions/${response.data.id}`);
    } catch (error) {
      toast.error(describeError(error));
    }
  }

  return (
    <div className="flex flex-col gap-5 pb-16">
      <PageIntro
        title="Generate questions"
        description="Pick a specialty, tool or skill and a level, agree the topics, and the AI writes and checks the questions in the background. Nothing reaches the exam until a run is approved."
      />
      <UsageBar />

      <StartHere onPick={pickStartHere} />

      {/* ---------- 1. What the questions are for ---------- */}
      <section className={cardClass}>
        <h2 className="font-display text-lg font-semibold text-brand-navy">
          What are the questions for?
        </h2>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <span className={labelClass}>Type</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {ITEM_TYPES.map((type) => (
                <button
                  key={type.key}
                  type="button"
                  onClick={() => changeType(type.key)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold ${
                    itemType === type.key
                      ? "border-brand-navy bg-brand-navy text-white"
                      : "bg-card text-muted-foreground"
                  }`}>
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="min-w-0">
            <span className={labelClass}>{itemTypeLabel(itemType)}</span>
            <div className="mt-2">
              <ItemPicker itemType={itemType} value={item} onChange={setItem} />
            </div>
          </div>

          <div className="min-w-0">
            <span className={labelClass}>Level</span>
            <select
              className={`${selectClass} mt-2 w-full`}
              value={levelCode}
              onChange={(event) => setLevelCode(Number(event.target.value))}>
              {LEVELS.map((level) => (
                <option key={level.code} value={level.code}>
                  L{level.code} · {level.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <span className={labelClass}>Number of questions</span>
            <div className="mt-2 flex gap-2">
              {COUNT_CHOICES.map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() => changeMaxQuestions(count)}
                  className={`rounded-full border px-3.5 py-1 text-sm ${
                    maxQuestions === count
                      ? "border-brand-navy bg-brand-navy text-white"
                      : "bg-card"
                  }`}>
                  {count}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- 2. Topic plan ---------- */}
      <section className={cardClass}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-brand-navy">
              Topic plan
            </h2>
            <p className="mt-1 max-w-[65ch] text-sm text-muted-foreground">
              Each topic is written separately, which keeps the questions
              varied. Rename, re-balance, remove or add topics, or let the AI
              suggest them for this item and level.
            </p>
          </div>
          <Button
            variant="outline"
            disabled={!item || suggestState.isLoading}
            onClick={suggestTopics}>
            <Sparkles className="mr-1.5 h-4 w-4" />
            {suggestState.isLoading ? "Thinking…" : "Suggest topics"}
          </Button>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          {topics.map((topic, index) => (
            <div
              key={index}
              className="grid grid-cols-[1fr_auto_auto] items-center gap-2 rounded-lg border p-2">
              <input
                className={inputClass}
                aria-label="Topic name"
                value={topic.name}
                onChange={(event) =>
                  changeTopic(index, "name", event.target.value)
                }
              />
              <input
                type="number"
                min={0}
                max={maxQuestions}
                aria-label="Number of questions"
                className={`${inputClass} w-20 text-center`}
                value={topic.count}
                onChange={(event) =>
                  changeTopic(index, "count", event.target.value)
                }
              />
              <button
                type="button"
                aria-label="Remove topic"
                onClick={() => removeTopic(index)}
                className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-700">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <form
          onSubmit={addTopic}
          className="mt-2 grid grid-cols-[1fr_auto] gap-2">
          <input
            className={inputClass}
            placeholder="Add your own topic"
            value={newTopic}
            onChange={(event) => setNewTopic(event.target.value)}
          />
          <Button type="submit" variant="outline">
            <Plus className="mr-1 h-4 w-4" /> Add topic
          </Button>
        </form>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-dashed pt-4">
          <span
            className={`text-sm font-semibold ${
              total >= 1 && total <= maxQuestions
                ? "text-emerald-700"
                : "text-red-700"
            }`}>
            {total} of {maxQuestions} questions at most
            {total > maxQuestions ? ` (${total - maxQuestions} too many)` : ""}
          </span>
          <Button
            disabled={!canCreate}
            onClick={create}
            className="bg-brand-orange text-brand-navy hover:bg-brand-orange-600">
            {createState.isLoading
              ? "Creating…"
              : `Generate ${total} questions`}
          </Button>
        </div>
        {!item && (
          <p className="mt-2 text-right text-xs text-muted-foreground">
            Pick an item first.
          </p>
        )}
      </section>

      {/* ---------- 3. Every run ---------- */}
      <RunList />
    </div>
  );
}

/** All runs, newest first, with a status filter and paging. */
function RunList() {
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);

  // Refresh every 30 seconds so runs move from "being written" to "ready" on their own
  const { data, isLoading, isError, error } = useGetCbtRunsQuery(
    { status: status, page: page, limit: 20 },
    { pollingInterval: 30000 },
  );

  const runs = data && data.data ? data.data : [];
  const lastPage = data && data.lastPage ? data.lastPage : 1;

  return (
    <section className={cardClass}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-brand-navy">
          Runs
        </h2>
        <select
          className={selectClass}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}>
          <option value="">All statuses</option>
          {Object.keys(RUN_STATUS).map((key) => (
            <option key={key} value={key}>
              {RUN_STATUS[key].label}
            </option>
          ))}
        </select>
      </div>

      {isLoading && (
        <p className="mt-4 text-sm text-muted-foreground">Loading runs…</p>
      )}
      {isError && (
        <p className="mt-4 text-sm text-red-700">{describeError(error)}</p>
      )}
      {!isLoading && !isError && runs.length === 0 && (
        <p className="mt-4 text-sm text-muted-foreground">No runs yet.</p>
      )}

      {runs.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">Item</th>
                <th className="py-2 pr-3">Level</th>
                <th className="py-2 pr-3 text-right">Questions</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id} className="border-t">
                  <td className="py-2 pr-3 text-muted-foreground">{run.id}</td>
                  <td className="py-2 pr-3">
                    <Link
                      to={`/cbt/questions/${run.id}`}
                      className="hover:underline">
                      <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {itemTypeLabel(run.item_type)}
                      </span>
                      <span className="font-semibold text-brand-navy">
                        {runItemName(run)}
                      </span>
                    </Link>
                  </td>
                  <td className="py-2 pr-3">{levelLabel(run.level_code)}</td>
                  <td className="py-2 pr-3 text-right">{run.question_count}</td>
                  <td className="py-2 pr-3">
                    <RunStatusPill status={run.status} />
                  </td>
                  <td className="py-2 text-muted-foreground">
                    {run.created_at}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className="text-muted-foreground">
            Page {page} of {lastPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </section>
  );
}
